/**
 * AuthContext — current user + login/logout, backed by the HttpOnly cookie
 * session. Ported from the Receipts app (client/src/context/AuthContext.js),
 * trimmed for the FPS Maps all-access model (no roles). On mount it probes
 * /auth/me; a 401/403 just means "not logged in" (show login), any other error
 * is surfaced as a connectivity problem.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Check for an existing session on mount (cookie is sent automatically).
  useEffect(() => {
    api.get('/auth/me')
      .then(userData => {
        setUser(userData);
        setAuthError(null);
        setLoading(false);
      })
      .catch((err) => {
        setUser(null);
        // 401/403 is expected when not logged in — just show the login page.
        // Anything else means we couldn't reach the API.
        if (err.status === 401 || err.status === 403) {
          setAuthError(null);
        } else {
          setAuthError('Unable to connect to the server. Please check your connection and try again.');
        }
        setLoading(false);
      });
  }, []);

  const login = useCallback((userData) => {
    // Token lives in the HttpOnly cookie — nothing to store client-side.
    setUser(userData);
    setAuthError(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore — we're logging out regardless.
    }
    setUser(null);
  }, []);

  const value = { user, loading, authError, login, logout, isAuthenticated: !!user };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
